import { useCallback, useMemo, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Combobox,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  Panel,
  SegmentedControl,
  type MultiComboboxOption,
  type SegmentedControlOption,
} from '@/components/ui';
import { DashboardFilterBar, FilterSlot } from '@/components/dashboards/DashboardFilterBar';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { CONVERSION_APPROXIMATE_NOTE, formatCompactMoney } from '@/lib/targets';
import { REVENUE_CURRENCIES, type RevenueCurrency } from '@/lib/use-revenue-analytics';
import { RevenueCycleStrip } from '@/components/dashboards/revenue-company/RevenueCycleStrip';
import {
  keyListParam,
  parseKeyList,
} from '@/components/dashboards/revenue-company/revenue-company-shared';
import { useRevenueProducts } from './use-revenue-products';
import {
  ALL,
  asProductOrder,
  cycleTotalsOf,
  excludedProductCount,
  groupProducts,
  regionKeyOf,
  regionOptionsOf,
  scopeOf,
  scopeCyclesOf,
  todaysCycleIdOf,
  sortProductGroups,
  taxMeasureLabel,
  totalOf,
  unstatedUnitRowCount,
  type ProductGroup,
  type ProductSortKey,
} from './revenue-product';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { RevenueProductHeadline } from './RevenueProductHeadline';
import { RevenueProductNotices } from './RevenueProductNotices';
import { RevenueProductTable, type ProductTableEmptyReason } from './RevenueProductTable';

/**
 * THE **Revenue by Product** TAB OF THE MANAGEMENT DASHBOARD.
 *
 * *"A complete analysis of how our products are performing — which are best
 * sellers"* — owner. Five layers, largest question first:
 *
 *   the control bar      the currency, the cycle and the billing region, always
 *                        visible; two narrowing controls behind a disclosure
 *   the notices          what money is in NO figure below, and why — rendered
 *                        only when there is some, which is not the usual case
 *   four headline tiles  how much, across how many rows, the leading CATALOGUED
 *                        product, and how much is attributed to no product
 *   the cycle strip      what each cycle contributed — and the cycle picker
 *   the product table    four columns, its filters and its ordering on the
 *                        column headers
 *
 * ⚠️ **NO `PageHeader` AND NO PERMISSION CHECK.** `ManagementDashboardPage` owns
 * the title, the tab strip and the gate; this is the panel it mounts. It takes
 * no props and owns its own data, so the page has nothing to wire and nothing
 * to keep in step.
 *
 * ── ⛔ THE CYCLE COMPARISON WAS REMOVED — owner, 2026-09-11 ────────────────
 *
 * *SUPERSEDED: a sixth layer, a "Growth and decline · cycle against cycle" panel
 * with `Later cycle` / `Earlier cycle` pickers and a permanent amber caption
 * about the part-elapsed cycle; a `Movement` column headed with the two cycle
 * names; a `By movement` arrangement grouping rows into five outcome sections;
 * an `Outcome` filter; and two orderings derived from it.* **All of it is
 * deleted rather than hidden**, along with `RevenueProductComparison.tsx`,
 * `movementBetween`, `groupByMovement` and the `ProductGroup.movement` field.
 *
 * ⚠️ **WHAT LEAVES WITH IT, said plainly because nothing on screen will:** this
 * tab can no longer answer *which products are growing and which are
 * declining*. **Top Movers is the tab that answers that**, against companies,
 * and it keeps its own copy of the vocabulary (`DIRECTION_META`) that this one
 * used to import.
 *
 * ── 🔴 THE CURRENCY CONVERTS. IT IS A BASE, NOT A FILTER — owner, 2026-09-14 ─
 *
 * ⚠️ *SUPERSEDED: "**ONE CURRENCY AT A TIME. IT IS THE DESIGN, NOT A FILTER.**
 * There is no exchange rate anywhere in this system, so two currencies are never
 * added … a product sold in two currencies appears under both … switching
 * currency HIDES PRODUCTS."* The server prices every paid line at **its own
 * invoice's `bill_date`** and sums the lot into one base, so this control now
 * asks *"show me the business in dollars"* rather than *"show me the dollar part
 * of the business"*. **The ability to see one currency alone has gone**, and
 * that was the trade the owner took. ⚠️ *SUPERSEDED 2026-09-18, the scope and not
 * the trade: "⛔ **This tab only** — Revenue by Company and the Sheets mirror
 * still filter."* **Revenue by Company and Top Movers convert too now**, on the
 * same instruction and through the same `measuredInCurrency` echo. ⛔ **The
 * Sheets mirrors still filter** and are the backend's own surfaces.
 *
 * Three consequences, each handled where it arises:
 *
 * 1. **The unit is the SERVER's echo, never the picker's value** —
 *    `figuresCurrency` below. An API predating `baseCurrency` ignores it and
 *    answers an ordinary `200` of unconverted rows, and labelling those `GBP`
 *    is the one way this change fails, silently, on money.
 * 2. **The "Also sold in" mark is withdrawn**, because the sibling rows it
 *    pointed at have been summed into the row beside it. Measured before
 *    withdrawal: 19 of 28 products carried it.
 * 3. **A figure whose unit was never recorded is still withheld from every
 *    total** (`d_invoices.currency` is nullable, so `{total: "9.00",
 *    totalCurrency: null}` is reachable) and is counted and named instead — a
 *    line with no currency cannot be converted either, so this outlives the
 *    filter it was written for.
 *
 * ⚠️ **WHAT THE CONVERSION CANNOT PRICE IS REPORTED, NOT DROPPED.** A line with
 * no rate stored for its own bill date is left out of every figure rather than
 * counted at par, and `meta.unpricedLines` says how many. It is `0` today —
 * every line has a rate at its bill date — and it exists so the day that stops
 * being true is a sentence on screen rather than a total that quietly shrank.
 * ⛔ **Its `null` is "nothing was examined" and is never folded into that `0`**:
 * one says the report is whole, the other says nobody looked.
 *
 * ── ⚠️ WHERE EACH CONTROL RUNS, WHICH IS NOT A DETAIL ─────────────────────
 *
 * **A row is `(billing region, product)` and carries no company at all**, so the
 * company search cannot be applied in the browser — there is nothing here to
 * match. It and the **base currency** are the two controls that cost a request;
 * ⚠️ *SUPERSEDED 2026-09-14: "the company search is the ONE control that goes to
 * the server"* — the currency joined it the day the arithmetic moved to the
 * server. Everything else narrows what came back, including the band and the
 * cycle, which the endpoint would also accept: applying them here costs no
 * request and keeps every column on the cycle strip. **The split is stated on
 * screen**, because the two behave differently under a slow connection.
 *
 * ⚠️ **`page`, `perPage`, `sort` AND `order` ARE A 422** on this endpoint. The
 * row set is bounded by the catalogue rather than by the data, so there is no
 * pager and nothing is truncated — see `src/lib/types.ts`. That is also what
 * makes sorting in the browser honest here; see {@link sortProductGroups}.
 *
 * ── ⚠️ WHAT THESE FIGURES ARE ─────────────────────────────────────────────
 *
 * **Paid invoices only, cash collected INCLUDING TAX** (owner decision
 * 2026-10-16). ⚠️ *SUPERSEDED 2026-10-16: "EX-TAX — the server sums
 * `line_subtotal` … do not reconcile with the Financial Analytics tab".* The
 * request sends `measure=inclTax`; every label reads `taxLabel`, derived from
 * the server's `meta.measure` echo, so an API that answers ex-tax is labelled
 * ex-tax.
 */

/* -------------------------------------------------------------------------
 * The URL
 * --------------------------------------------------------------------- */

/**
 * The query keys this tab owns.
 *
 * ⚠️ **PREFIXED, BECAUSE THE PAGE AND ITS SIBLING TABS SHARE ONE QUERY
 * STRING.** `ManagementDashboardPage` writes `?tab=`, and the other three views
 * own their own filters; an unprefixed `cycle` or `q` would be one tab silently
 * reading another's state. The prefix is the whole guard, so a new key here
 * takes it too.
 *
 * ⚠️ **FIVE KEYS WHERE THERE WERE ELEVEN.** `rpLater`, `rpEarlier`, `rpView`,
 * `rpDir` and `rpMin` went with the comparison; `rpSort` went to the column
 * headers. A saved link carrying any of them still opens — an unknown key is
 * simply not read — but it no longer does anything, which is the quiet half of
 * removing a feature and is why it is written down here.
 */
const PARAM = {
  currency: 'rpCurrency',
  search: 'rpQ',
  region: 'rpRegion',
  cycle: 'rpCycle',
  uncoded: 'rpUncoded',
} as const;

/**
 * 🔴 **GBP, AND DELIBERATELY NOT `DEFAULT_REVENUE_CURRENCY` — owner, 2026-09-14.**
 *
 * ⚠️ *SUPERSEDED 2026-09-18, the REASON and not the value: "That shared constant
 * is USD and **must stay USD** … it is still exactly right for Revenue by Company
 * and Top Movers, which **filter** … ⛔ **DO NOT 'HARMONISE' THE TWO.** Moving
 * the shared default to GBP would open the two filtering tabs on 7% of the
 * money, which is the failure that constant was written to prevent."*
 *
 * **Those two tabs convert now**, so the argument that made the shared constant
 * USD is spent there exactly as it was spent here, and it **moved to GBP** on the
 * owner's instruction. The two agree today.
 *
 * ⛔ **THEY STILL ARE NOT MERGED, AND AGREEING IS NOT A REASON TO MERGE THEM.**
 * This tab owns its own URL key (`rpCurrency`), its own parser and its own
 * `.strict()` query; one constant that three tabs must agree on for ever is a
 * coupling nobody asked for, and the next divergence would be resolved by
 * whichever tab was edited last. **They arrive at the same answer rather than
 * sharing one**, which is what lets either move without moving the other.
 *
 * ⚠️ **AND THE ARGUMENT IS WORTH KEEPING FOR THE NEXT SURFACE, because it is
 * about FILTERING and not about GBP**: a tab that narrows to one currency must
 * open on the one most of the business is billed in, and a tab that converts must
 * open on the one it is reported in. **Ask which a surface does before copying
 * either default.**
 */
const DEFAULT_BASE_CURRENCY: RevenueCurrency = 'GBP';

/**
 * ⚠️ **UNKNOWN INPUT FALLS BACK RATHER THAN THROWING OR RENDERING NOTHING.**
 * These land in a URL that gets typed, pasted and truncated by chat clients,
 * and a dashboard that answers a mangled address with a blank page is a support
 * report.
 *
 * ⚠️ **`?rpCurrency=USD` STILL WORKS AND NOW MEANS SOMETHING ELSE.** It used to
 * say *"show me the dollar part of the business"* and says *"show me the
 * business in dollars"*. Nothing about the link breaks, the figures under it are
 * larger, and no parser can tell the two readings apart — which is why the
 * validating parse is kept rather than widened: an unrecognised code must still
 * fall back rather than be forwarded to a `.strict()` query as a 422.
 */
/**
 * The server's echo, narrowed — `null` where it is absent **or where it names a
 * currency this tab cannot render**.
 *
 * 🔴 **AN UNRECOGNISED ECHO IS TREATED AS UNCONVERTED, NOT COERCED AND NOT
 * DISPLAYED.** Every render prop on this tab takes {@link RevenueCurrency}, and
 * the two ways out of that are both worse than this one: casting the string
 * through puts a code on screen the rest of the page cannot format, and falling
 * back to the picker's value LABELS the response with a unit it is not in —
 * which is the single failure `measuredInCurrency` exists to prevent, arriving
 * through a type assertion instead of through a request.
 *
 * ⚠️ **Unreachable against a conforming server**, which can only echo a code it
 * was sent, and the picker sends one of three. It is written down because the
 * reachable-looking alternative is the dangerous one.
 */
function asRenderableCurrency(raw: string | null): RevenueCurrency | null {
  return raw !== null && (REVENUE_CURRENCIES as readonly string[]).includes(raw)
    ? (raw as RevenueCurrency)
    : null;
}

function parseCurrency(raw: string | null): RevenueCurrency {
  return raw !== null && (REVENUE_CURRENCIES as readonly string[]).includes(raw)
    ? (raw as RevenueCurrency)
    : DEFAULT_BASE_CURRENCY;
}

/**
 * The currency control's options.
 *
 * ⚠️ **DERIVED FROM `REVENUE_CURRENCIES`, never written out again.** A second
 * list offers exactly the currencies somebody remembered on the day, so a
 * fourth added to the library and not here becomes unreachable while its
 * figures are perfectly computable.
 */
const CURRENCY_OPTIONS: readonly SegmentedControlOption<RevenueCurrency>[] =
  REVENUE_CURRENCIES.map((code) => ({ value: code, label: code }));

/**
 * The uncatalogued bucket: listed, or held back.
 *
 * ⚠️ **TWO LABELLED SEGMENTS RATHER THAN A CHECKBOX**, because both states have
 * to be readable at a glance. An unticked box states nothing — the reader has to
 * know what the absent tick would have meant — and on the one control that
 * removes three fifths of the money from the page, "states nothing" is the wrong
 * default. See the control's own comment in the advanced panel.
 */
const UNCODED_OPTIONS: readonly SegmentedControlOption<'include' | 'exclude'>[] = [
  { value: 'include', label: 'Included' },
  { value: 'exclude', label: 'Excluded' },
];

/**
 * 🔴 **THE ONLY WAY A COLUMN MAY CLAIM A SORT, AND IT IS A COMPILE-TIME CHECK.**
 *
 * `ColumnSpec.sortKey` is a bare `string`. A typo there is accepted, the funnel
 * renders its Ascending/Descending pair, every click updates the hook's sort
 * state — and `sortProductGroups` does not recognise the key, so **nothing
 * reorders, nothing errors and nothing on screen says why**. That is exactly the
 * defect `CompaniesPage` shipped with two dead sort options, reached from the
 * other end.
 *
 * Routing every `sortKey` through this helper makes an unbacked one a BUILD
 * failure: {@link ProductSortKey} is the union `sortProductGroups` switches over
 * exhaustively, so the two cannot come apart.
 */
function sortable(key: ProductSortKey): { sortKey: ProductSortKey } {
  return { sortKey: key };
}

export function RevenueByProductPanel(): ReactElement {
  /*
   * Layout manager (owner 2026-10-20): the headline, the cycle strip and the
   * product table are movable per person; the filter bar and the notices stay
   * fixed above them. The ids are stored per user — never rename them. Called
   * before the early return below, so the hook order never changes.
   */
  const layout = usePageLayout('management-dashboard.revenue-product', [
    { id: 'headline', label: 'Summary' },
    { id: 'cycle-strip', label: 'Revenue by cycle' },
    { id: 'products', label: 'Revenue by product' },
  ]);
  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT rather than options — `useSearchParams({ replace: true })` injects a
   * literal `replace=true` into the query string and then into everything
   * parsed out of it. The `{ replace: true }` belongs on the setter.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);

  const currency = parseCurrency(params.get(PARAM.currency));
  const search = params.get(PARAM.search) ?? '';
  /* Several bands (owner 2026-10-28), CSV in the URL; `[]` = all; an old single value parses. */
  const regionKeysCsv = params.get(PARAM.region) ?? ALL;
  const regionKeys = useMemo(() => parseKeyList(regionKeysCsv), [regionKeysCsv]);
  const requestedCycle = params.get(PARAM.cycle) ?? ALL;
  /* Absent means included: the honest default is that nothing is hidden. */
  const includeUncoded = params.get(PARAM.uncoded) !== '0';

  /*
   * 🔴 **TWO QUERY PARAMETERS: THE SEARCH AND THE BASE CURRENCY.** ⚠️
   * *SUPERSEDED 2026-09-14: "THE SEARCH IS A QUERY PARAMETER AND EVERY OTHER
   * CONTROL IS NOT."* No company reaches the browser on a row, so the search
   * cannot be applied here; and the conversion is arithmetic only the server can
   * do, because only it holds the rates and the per-line bill dates. The hook
   * debounces the search alone and aborts the read it replaces.
   */
  const {
    rows,
    cycles,
    loading,
    error,
    unmatchedLines,
    convertedTo,
    unpricedLines,
    taxMeasure,
    reload,
  } = useRevenueProducts(search, currency);
  /* From the server's `meta.measure` echo, never from the request. */
  const taxLabel = taxMeasureLabel(taxMeasure);

  /*
   * 🔴 **THE UNIT EVERY FIGURE ON THIS TAB IS IN, RESOLVED ONCE AND READ FROM
   * THE SERVER'S ECHO.** This is the single line that stands between the tab and
   * its one silent failure: an API predating `baseCurrency` ignores the key,
   * answers a perfectly ordinary `200` of UNCONVERTED rows, and a panel labelling
   * those from `currency` would print `GBP` over dollars on the tab whose figures
   * a finance reader adds up.
   *
   * ⚠️ **Falling back to `currency` restores the OLD behaviour exactly**, filter
   * and all — `amountIn` then admits only cells in the picked currency, which is
   * what this tab did up to 2026-09-14. That is the honest degradation: fewer
   * products, correctly labelled, rather than more products wrongly labelled.
   */
  const measuredIn = asRenderableCurrency(convertedTo);
  const converted = measuredIn !== null;
  const figuresCurrency: RevenueCurrency = measuredIn ?? currency;

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      // A default is spelled by the key's ABSENCE — one canonical URL per view.
      if (value === ALL) next.delete(key);
      else next.set(key, value);
      /*
       * `replace: true`: changing a filter is adjusting what is on screen, not
       * navigating, and a history entry per keystroke would make Back mean
       * "undo one filter".
       */
      setSearchParams(next, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  /* ── the cycle scope ─────────────────────────────────────────────────── */

  /*
   * ⚠️ **RESOLVED AGAINST THE DATA, and an unknown cycle reads as "all cycles"
   * EVERYWHERE.** The cycle list is admin-maintained, so a saved link can name
   * one that has since been retired. Resolving once here is what stops the
   * control sitting blank while the figures below quietly cover everything.
   *
   * ⚠️ *SUPERSEDED 2026-09-13, one word: "what stops the `<select>` sitting
   * blank".* The control is a `Combobox` now, and an unmatched value there
   * falls back to the PLACEHOLDER rather than to a blank box — "All cycles"
   * printed over figures narrowed to nothing, which is worse than the blank it
   * replaced. **This resolution is therefore MORE load-bearing than when it was
   * written.** The billing region below has no such resolution and injects its
   * held value instead; doing neither is what produces a confident wrong label.
   */
  const cycleKey = useMemo(
    () => (cycles.some((cycle) => cycle.name === requestedCycle) ? requestedCycle : ALL),
    [cycles, requestedCycle],
  );
  const scope = useMemo(() => scopeOf(cycles, cycleKey), [cycles, cycleKey]);
  /* The company matrix's columns, and the cycle containing today (its bound). */
  const scopeCycles = useMemo(() => scopeCyclesOf(cycles, scope), [cycles, scope]);
  const todaysCycleId = useMemo(() => todaysCycleIdOf(cycles), [cycles]);
  const scopeLabel = cycleKey === ALL ? 'All cycles' : cycleKey;

  /* ── narrowing, in the browser ───────────────────────────────────────── */

  /** Every band present in the data, so the picker cannot offer an empty one. */
  const regionOptions = useMemo(() => regionOptionsOf(rows), [rows]);

  /*
   * 🔴 **THE HELD BAND IS INJECTED BACK WHEN THE DATA DOES NOT CONTAIN IT —
   * added 2026-09-13 WITH the `Combobox`, and it is the one thing that had to
   * change beyond the control itself.**
   *
   * `regionKey` comes straight off the URL and is deliberately NOT resolved
   * against the data the way `cycleKey` above is — narrowing to a band nobody
   * traded in is a real question with a real answer (nothing), so resolving it
   * away would silently widen a shared link. The cost is that the value can sit
   * off the loaded list, and the two controls answer that differently:
   *
   *   a `<select>`  renders BLANK          — visibly odd, and it was
   *   a `Combobox`  renders its PLACEHOLDER — "All billing regions", over a
   *                                           table narrowed to nothing
   *
   * ⚠️ **The second is strictly worse: a confident wrong label rather than an
   * obvious absence**, and it is what a straight swap would have shipped. It
   * bites harder on this tab than on Revenue by Company, because `bandRows`
   * runs BEFORE grouping — an unmatched band empties every product group rather
   * than shortening one list.
   *
   * ⚠️ **Kept in step with the twin in `RevenueByCompanyPanel` by hand, and
   * deliberately not shared**: the two `regionOptionsOf` are already separate
   * functions over different row types, in two folders that are free to
   * diverge. What must not diverge is the DECISION, which is why both carry the
   * reasoning rather than one pointing at the other.
   */
  const regionPickerOptions = useMemo<MultiComboboxOption[]>(() => {
    const base: MultiComboboxOption[] = regionOptions.map(({ value, label }) => ({ value, label }));
    const listed = new Set(base.map((option) => option.value));
    /*
     * The KEY is the label, because there is no row to read a label off — that
     * is the whole of what "not in the data" means here. The hint is a fact
     * about the data, not advice, so it is the kind of sentence the
     * no-helper-text rule keeps.
     */
    const held = regionKeys
      .filter((key) => !listed.has(key))
      .map((key) => ({ value: key, label: key, hint: 'No product loaded is in it' }));
    return held.length === 0 ? base : [...base, ...held];
  }, [regionOptions, regionKeys]);

  /*
   * 🔴 **THE BAND NARROWS ROWS AND MUST RUN BEFORE GROUPING.** A product sold
   * into three regions would otherwise keep all three regions' money while
   * claiming to be filtered to one.
   *
   * ⚠️ **THIS IS WHY THE BILLING REGION IS A CONTROL UP HERE AND NOT A FUNNEL ON
   * A COLUMN.** `useTableFilters` filters the array it is handed, which by then
   * is one row per PRODUCT with every band's money already summed into it — so a
   * region funnel could only drop whole products, leaving each survivor carrying
   * money it did not take in the chosen region. The figure would be wrong while
   * looking entirely right.
   */
  const bandRows = useMemo(() => {
    const wanted = new Set(regionKeys);
    return wanted.size === 0 ? rows : rows.filter((row) => wanted.has(regionKeyOf(row)));
  }, [rows, regionKeys]);

  /* ⚠️ **`figuresCurrency`, NEVER `currency`, from here down.** Everything below
   * reaches the wire through `amountIn`, whose parameter is the unit the FIGURES
   * are in — the server's echo where it converted, the picker's value where it
   * did not. Passing the request instead is the one mistake that mislabels
   * money, and it typechecks. */
  const cycleTotals = useMemo(
    () => cycleTotalsOf(bandRows, cycles, figuresCurrency),
    [bandRows, cycles, figuresCurrency],
  );

  const allGroups = useMemo(
    () => groupProducts(bandRows, cycles, scope, figuresCurrency, converted),
    [bandRows, cycles, scope, figuresCurrency, converted],
  );

  /*
   * ⚠️ **TAKEN BEFORE THE BUCKET IS EXCLUDED, SO THE TILE STILL REPORTS IT WHEN
   * A READER HAS EXCLUDED IT.** Computed from the filtered set it would be
   * `null` the moment the toggle went off, and the tile would say "None in
   * scope" — which is a claim about the data made by a control.
   */
  const uncodedTotal = useMemo(
    () => totalOf(allGroups.filter((group) => group.isUncoded)),
    [allGroups],
  );

  const scopedGroups = useMemo(
    () => (includeUncoded ? allGroups : allGroups.filter((group) => !group.isUncoded)),
    [allGroups, includeUncoded],
  );

  /* ── the column filters and the ordering ─────────────────────────────── */

  /**
   * ⚠️ **EVERY COLUMN HERE IS `page`-SCOPED, AND ON THIS TAB THAT IS NOT A
   * NARROWER CLAIM THAN `server`.** The scope flag exists because every other
   * list in this app is server-paginated, so a value list built from 20 loaded
   * rows only looks dataset-wide. `GET /revenue-tracker/products` has no pager
   * at all — one request returns the whole row set — so the loaded page IS the
   * dataset and a page-scoped filter really does filter everything there is.
   *
   * ⚠️ **`revenue` LISTS EVERY DISTINCT AMOUNT, which is noise on a money
   * column, and it is kept anyway** — the funnel is also where the
   * Ascending/Descending pair lives, and that ordering is the control the tab
   * most needs. `options: []` was the alternative and it renders *"No values on
   * this page"*, a sentence that is simply false.
   */
  const columns = useMemo<ColumnSpec<ProductGroup>[]>(
    () => [
      {
        key: 'product',
        label: 'Product',
        /* The label the cell actually prints, so a ticked value and the row a
         * reader is looking at cannot say different things. */
        get: (group) => group.label.text,
        ...sortable('name'),
      },
      {
        key: 'cycles',
        label: 'Cycles sold in',
        /* ⚠️ Counted over EVERY cycle on record, not over the chosen scope —
         * see `ProductGroup.byCycle`. The heading says "sold in" for that
         * reason: "Cycles" alone reads as a count within the scope above. */
        get: (group) => group.activeCycles,
        numeric: true,
        ...sortable('cycles'),
      },
      {
        key: 'revenue',
        label: 'Paid revenue',
        get: (group) => group.total,
        /*
         * 🔴 **COMPACT, TO MATCH THE CELL — 2026-09-13.** *SUPERSEDED:
         * `` `${currency} ${formatDecimalString(value)}` ``.*
         *
         * ⚠️ **THIS IS THE FUNNEL'S OPTION LABEL, NOT THE CELL**, and it is
         * compacted because a funnel is a MATCHING control: the reader saw
         * `GBP £20.00K` in the row and comes here to tick it. Leaving this as
         * the long decimal would offer `20,000.00` against a cell reading
         * `£20.00K` and the value they came for would be unfindable — which is
         * the failure on every use, where the one below bites only sometimes.
         *
         * ⚠️ **ACCEPTED COST, STATED: TWO DISTINCT AMOUNTS CAN NOW SHARE A
         * LABEL.** `20,001.00` and `20,400.00` both read `£20.00K`, so the list
         * can show what looks like the same option twice. They tick
         * independently and each narrows correctly — the funnel keys on the
         * value from `get`, never on this string — so the cost is a reader who
         * cannot tell two options apart, not a filter that mis-matches.
         *
         * ⛔ **NO `title` IS REACHABLE HERE** — a `format` returns a string and
         * the option row is drawn by the primitive. The exact figure is not
         * lost from the PAGE: `Money` in `RevenueProductTable` carries it on
         * every cell's `title`, which is the guarantee. **If that attribute is
         * ever removed, this line loses its justification with it.**
         *
         * ⚠️ The code is still prefixed even though the compacted head carries
         * a symbol: `$` is four currencies, and an option list is read with no
         * column heading above it to say which.
         */
        format: (value) =>
          `${figuresCurrency} ${formatCompactMoney(value, figuresCurrency)}`,
        numeric: true,
        ...sortable('revenue'),
      },
    ],
    [figuresCurrency],
  );

  const table = useTableFilters(scopedGroups, columns);

  /*
   * ⚠️ **RESOLVED ONCE, AND AN UNRECOGNISED KEY BECOMES `null` RATHER THAN A
   * GUESS.** `null` is the opening order — largest paid revenue first — and
   * `orderNote` below is derived from THIS value rather than from `table.sort`,
   * so a key nothing can drive would show an arrow on a header while the
   * heading reads "ranked by paid revenue": a visible contradiction instead of a
   * silent no-op. The compile-time check in `sortable` is what makes that state
   * unreachable in the first place.
   */
  const order = useMemo(() => asProductOrder(table.sort), [table.sort]);

  const groups = useMemo(
    () => sortProductGroups(table.visibleRows, order),
    [table.visibleRows, order],
  );

  const total = useMemo(() => totalOf(groups), [groups]);

  /*
   * ⚠️ Taken over EVERY row, deliberately — it answers "what is missing because
   * of the CURRENCY", which used to be the one control whose effect a reader
   * could not see. See `excludedProductCount`, which records why this is an
   * UNCONVERTED-only measurement and why the panel passes `0` under conversion
   * rather than trusting it to compute one.
   */
  const excluded = useMemo(
    () => excludedProductCount(rows, figuresCurrency),
    [rows, figuresCurrency],
  );
  const unstated = useMemo(() => unstatedUnitRowCount(rows), [rows]);

  /**
   * 🔴 **HOW MANY ADVANCED CONTROLS DIFFER FROM THEIR DEFAULT — nothing else.**
   * `DashboardFilterBar`'s header is explicit that this number is the caller's
   * to compute, and that with the panel shut it is the only thing standing
   * between a reader and a narrowed figure read as the whole. On this tab that
   * matters more than on any other: **excluding the uncatalogued bucket removes
   * about three fifths of the money**, and it is one tick behind a disclosure.
   *
   * ⛔ **THE CURRENCY, THE CYCLE AND THE BILLING REGION ARE NOT COUNTED.** All
   * three are primary and permanently visible, and counting a control the reader
   * can see would make the badge overstate every ordinary read.
   *
   * ⛔ **THE COLUMN FILTERS ARE NOT COUNTED EITHER, and that is the same rule.**
   * A filtered column wears a filled funnel in its own header and the table's
   * footer names how many products it is holding back — so a badge on a
   * disclosure that does not contain them would be reporting on somewhere else.
   * They ARE cleared by Clear; see below.
   */
  const activeAdvanced = (search.trim() !== '' ? 1 : 0) + (includeUncoded ? 0 : 1);

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    /*
     * ⚠️ **EVERY NARROWING CONTROL ON THE TAB, primary ones included.** The
     * cycle and the region are not counted above, but both ARE filters and the
     * table's empty state offers this button as the way out of one — so a reader
     * who presses Clear on an empty cycle must not be handed the same empty
     * table. The currency is the one exception and cannot be otherwise: it has
     * no "all" state, so clearing to it would be clearing to something
     * unrenderable.
     */
    for (const key of [PARAM.search, PARAM.region, PARAM.cycle, PARAM.uncoded]) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
    /*
     * 🔴 **THE COLUMN FILTERS LIVE IN THE HOOK, NOT IN THE URL, so clearing the
     * query string alone would leave them set.** That is the shape this button
     * exists to rescue a reader from — it is the action offered on an empty
     * table, and an empty table left empty by it is worse than no button.
     */
    table.clearAll();
  }, [searchParamsKey, setSearchParams, table]);

  /**
   * 🔴 **WHY AN EMPTY TABLE IS EMPTY — DECIDED HERE, BECAUSE ONLY HERE CAN THE
   * THREE BE TOLD APART.** From inside the table all three are one empty array.
   *
   * ⚠️ **THE SEARCH CASE IS TESTED FIRST AND AGAINST `rows`, NOT `groups`.**
   * `rows.length === 0` after a search means the SERVER matched no company —
   * across every currency and every cycle, since the term is the one filter it
   * applies. Reported as *"no products match these filters"* it would send a
   * reader to clear a currency or a band that had nothing to do with it.
   *
   * ⚠️ **`table.activeCount` IS PART OF `filtered`.** A column funnel that keeps
   * no rows leaves the same empty array as a currency nobody trades in, and
   * reporting that as *"No USD revenue to report"* is a statement about the
   * business made by a control.
   */
  const filtered =
    search.trim() !== '' ||
    cycleKey !== ALL ||
    regionKeys.length > 0 ||
    !includeUncoded ||
    table.activeCount > 0;

  const emptyReason: ProductTableEmptyReason =
    search.trim() !== '' && rows.length === 0
      ? { kind: 'no-search-match', term: search.trim() }
      : filtered
        ? { kind: 'filtered' }
        : { kind: 'no-revenue' };

  /** Nothing has arrived yet — the only state that earns a skeleton. */
  const awaitingFirstRead = loading && rows.length === 0;

  /* ── the read failed, and there is nothing behind it ─────────────────── */

  /*
   * ⚠️ **A FAILED READ WITH NOTHING ON SCREEN MUST NOT FALL THROUGH TO THE
   * TABLE'S EMPTY STATE.** That state says "no revenue in this currency", which
   * is a statement about the business; this is a statement about the request.
   * Reading one as the other is how a broken endpoint gets reported as a quiet
   * quarter. A failed REFRESH over figures already on screen is a different
   * matter — those stay, with a strip above them saying they are stale.
   */
  if (error !== null && rows.length === 0) {
    return (
      <Panel className="min-w-0">
        <EmptyState
          title="The revenue figures could not be loaded"
          description={error}
          action={
            <Button variant="secondary" onClick={reload}>
              Try again
            </Button>
          }
        />
      </Panel>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/*
        ── 🔴 THREE CONTROLS VISIBLE, TWO BEHIND A DISCLOSURE ─────────────────

        ⚠️ *SUPERSEDED, owner 2026-09-11: two visible and SEVEN behind the
        disclosure — a billing region, the uncatalogued toggle, an outcome
        filter, a minimum-revenue box, an arrangement switch and an "Order by"
        select, plus the search.* Four of those went with the cycle comparison;
        the ordering moved onto the column headers, where a reader reaches it by
        clicking the column they want to order by rather than by opening a panel
        and finding its name in a list.

        **Primary is what decides what a figure MEANS**: the unit, the window,
        and now the geography — all three change the money itself rather than
        which rows are listed, which is why the billing region was promoted out
        of the disclosure at the same time its funnel was ruled out. See
        `bandRows`.

        ⚠️ **THE PRICE OF HIDING THE UNCATALOGUED TOGGLE IS PAID BY
        `activeAdvanced`.** With the panel shut, that count is the only thing
        telling a reader the figures in front of them are three fifths short.
      */}
      <DashboardFilterBar
        rememberAs="revenue-product-more-filters"
        activeAdvanced={activeAdvanced}
        onClear={clearFilters}
        /*
         * ⚠️ **WHICH CONTROL RUNS WHERE, and it is not a detail.** A row carries
         * no company, so the search cannot be applied in the browser; and the
         * conversion needs rates and per-line bill dates that never reach it. So
         * TWO controls cost a request and the rest are instant — ⚠️ *SUPERSEDED
         * 2026-09-14: "the search … is the only one that costs a request"*. A
         * reader who assumes the currency is instant will misread a table that
         * has not caught up yet.
         *
         * ⚠️ **THE LIVE REGION IS MOUNTED EVEN WHILE IT IS EMPTY.** A
         * `role="status"` that appears only once there is something to say is a
         * region assistive tech has not been watching, so the announcement is
         * missed on the read it exists to report.
         */
        note={
          <>
            <span role="status" className="text-fg-subtle">
              {loading && rows.length > 0 ? 'Updating…' : ''}
            </span>
          </>
        }
        primary={
          <>
            {/*
              🔴 **THE CURRENCY IS THE UNIT OF EVERY FIGURE ON THIS TAB, so it is
              a segmented control rather than a `<select>`** — the three options
              are visible at once and the one in force is readable without
              opening anything. A reader who cannot see which currency they are
              looking at is a reader who will quote the wrong number.

              🔴 **SINCE 2026-09-14 IT PICKS A BASE TO CONVERT INTO RATHER THAN A
              CURRENCY TO FILTER TO, and the control looks identical either way.**
              `label="Currency"` is kept because that is what a reader calls it,
              and because the disclaimer under the tiles is what says the figures
              were converted — a control cannot carry that sentence and the
              no-helper-text rule forbids putting one under it. ⚠️ **Its `value`
              is the REQUEST and stays `currency`**; every FIGURE is labelled from
              `figuresCurrency`, which is the server's echo. The two differ only
              against an API predating the key, which is exactly when the
              difference matters.

              ⚠️ **`FilterSlot`, not `Field`.** `SegmentedControl` renders a
              labelled GROUP of buttons with no single focusable input, so a
              `<label for>` would point at nothing; the slot's plain span and the
              group's own `aria-label` say the same word.
            */}
            <FilterSlot label="Currency">
              <SegmentedControl
                label="Currency"
                value={currency}
                options={CURRENCY_OPTIONS}
                onChange={(next) => setParam(PARAM.currency, next)}
              />
            </FilterSlot>

            {/*
              ⚠️ **PRIMARY, THOUGH IT IS A FILTER.** It is the WINDOW every
              figure covers, and the tab's second-largest panel is a strip of
              cycle totals that sets this same value — a control whose state is
              rendered as a row of bars cannot also be hidden behind a
              disclosure, or the two would disagree about whether a filter is in
              force.
            */}
            {/*
              ✅ **BOTH DROPDOWNS ON THIS TAB ARE SEARCHABLE `Combobox`es —
              owner's decision, 2026-09-13.** *SUPERSEDED: two `ui/Select`s with
              inline `<option>` lists.* Every non-table-column filter dropdown in
              the app takes one shape, and the owner chose that consistency
              explicitly over the "a handful of options needs no search box"
              reading.

              ⛔ **THE CURRENCY ABOVE AND THE THREE OTHER SEGMENTED CONTROLS ON
              THIS TAB ARE NOT PART OF THAT AND MUST NOT BECOME DROPDOWNS** —
              each says in its own comment why the choice in force has to be
              readable without opening anything.

              ⚠️ **`Combobox` takes no `className`, so the widths move to
              wrappers.** `max-w-full` is what keeps a fixed width from
              overflowing the bar at 320px.

              ⚠️ **`ALL` is `''`, which is what lets "All cycles" be
              `emptyOptionLabel` — and the same string must ALSO be the
              `placeholder`.** `Combobox` looks the trigger's label up in
              `options`, which the empty row is not part of, so without it an
              unfiltered control reads "Select…". The reference is `DealsPage`'s
              four filters.

              ⛔ **NO `loading` PROP, deliberately.** `Combobox` renders
              "Searching…" INSTEAD OF the list while it is set, which here would
              hide the "All …" row — the only row that is always correct, and the
              one a reader arriving on a filtered link needs in order to clear
              it. These lists are derived from rows already in the browser.
            */}
            <div className="w-48 max-w-full">
              <Field label="Cycle">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={cycleKey}
                    onChange={(value) => setParam(PARAM.cycle, value)}
                    options={cycles.map((cycle) => ({ value: cycle.name, label: cycle.name }))}
                    emptyOptionLabel="All cycles"
                    placeholder="All cycles"
                    searchPlaceholder="Search cycles…"
                  />
                )}
              </Field>
            </div>

            {/*
              🔴 **PROMOTED OUT OF THE DISCLOSURE, and it belongs here for the
              same reason the other two do: it changes what the figures ARE.**
              Narrowing to one band re-sums every product from that band's rows
              alone. It is also the one narrowing control that could not become a
              column funnel without lying — see `bandRows`.
            */}
            {/*
              ⚠️ **`regionPickerOptions`, NOT `regionOptions` — see the memo.**
              The held band is injected when the loaded rows do not contain it,
              because this value comes off the URL unresolved and a `Combobox`
              answers an unmatched value with its placeholder. "All billing
              regions" printed over an emptied table is the one outcome worse
              than the blank box this replaced.
            */}
            <div className="w-56 max-w-full">
              <Field label="Billing region">
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={regionKeys}
                    onChange={(values) => setParam(PARAM.region, keyListParam(values))}
                    options={regionPickerOptions}
                    placeholder="All billing regions"
                    searchPlaceholder="Search billing regions…"
                    summaryNoun={{ singular: 'billing region', plural: 'billing regions' }}
                  />
                )}
              </Field>
            </div>

            {/*
             * `order-last` puts the layout toolbar after the bar's own Filters
             * disclosure, which carries the row's `ml-auto`, so both sit at the
             * row's end.
             */}
            <div className="order-last flex items-end">
              <LayoutToolbar layout={layout} />
            </div>
          </>
        }
        advanced={
          <>
            <Field label="Search companies">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  className="w-56"
                  placeholder="Search companies…"
                  aria-describedby={describedBy}
                  value={search}
                  onChange={(event) => setParam(PARAM.search, event.target.value)}
                />
              )}
            </Field>

            {/*
              🔴 **THE MOST CONSEQUENTIAL CONTROL ON THE TAB, AND IT IS BEHIND A
              DISCLOSURE.** Excluding the bucket removes about three fifths of
              the money from every figure on the page — measured 2026-09-11: USD
              5,147,358.44 of it. Three things carry that, and none of them
              substitutes for the others:

                the count on the shut toggle   something is set
                the notice under this bar      WHAT is set, and what it costs
                the Uncatalogued tile          it reads "Excluded", not "0%"

              ⚠️ **A SEGMENTED PAIR RATHER THAN A CHECKBOX.** A checkbox states
              one of its two states and leaves the other to be inferred from an
              empty box, and it needed a two-line description to say which — 120
              characters of prose sitting in a row of controls. Two labelled
              segments say the state outright and stand the same height as the
              fields beside them.

              ⚠️ **IT IS NOT A COLUMN FUNNEL, though the bucket IS one row in the
              Product funnel's value list.** Unticking it there hides the row and
              takes its money out of the shares exactly as this does — what this
              control adds is the notice and the tile that explain the size of
              what just left.
            */}
            <FilterSlot label="Uncatalogued revenue">
              <SegmentedControl
                label="Uncatalogued revenue"
                value={includeUncoded ? 'include' : 'exclude'}
                options={UNCODED_OPTIONS}
                onChange={(next) => setParam(PARAM.uncoded, next === 'include' ? ALL : '0')}
              />
            </FilterSlot>
          </>
        }
      />

      {/*
        🔴 **EVERYTHING THIS TAB HAS TO SAY ABOUT MONEY THAT IS IN NO FIGURE
        BELOW.** They are measurements, not controls, and they belong between the
        controls and the figures they qualify.

        ⚠️ **IT RENDERS NOTHING WHEN THERE IS NOTHING TO SAY**, which is the
        ordinary state — and is what makes it affordable to show each one
        prominently when there is.
      */}
      <RevenueProductNotices
        currency={figuresCurrency}
        /*
         * ⚠️ **A FAILED REFRESH ONLY.** A read that failed with nothing behind
         * it has already replaced the whole tab above; passing it here too would
         * render a caveat about figures that are not on screen.
         */
        refreshError={error !== null && rows.length > 0 ? error : null}
        /*
         * ⚠️ **`uncodedTotal` IS TAKEN BEFORE THE BUCKET IS EXCLUDED**, so it
         * still has a figure to report at the moment the reader excludes it.
         * Computed from the filtered set it would be `null` exactly when this
         * notice is needed.
         */
        uncodedExcluded={includeUncoded ? null : uncodedTotal}
        unstatedProducts={unstated}
        unmatchedLines={unmatchedLines}
        /*
         * 🔴 **SUPPRESSED UNDER CONVERSION, AND NOT LEFT TO READ `0` ON ITS
         * OWN.** Its sentence ends *"they may have sold in another currency"* —
         * true while the control filtered, and a false statement now that every
         * currency is in the figures. The only row that could still reach it is
         * one carrying an UNSTATED unit, which `unstatedProducts` above reports
         * in the terms that actually apply to it. Two notices describing one row
         * in incompatible ways is worse than one.
         */
        excludedByCurrency={awaitingFirstRead || converted ? 0 : excluded}
        /*
         * 🔴 **THE COUNT THAT KEEPS CONVERSION HONEST.** A paid line with no rate
         * stored for its own bill date is left OUT of every figure rather than
         * priced at par, and this is the only thing on the page that says so. It
         * is `0` today — every line has a rate at its bill date — so this renders
         * nothing, which is what makes it affordable to render prominently the
         * day a gap appears.
         *
         * ⛔ **PASSED THROUGH AS `number | null`, NEVER COALESCED HERE.** `null`
         * is *nothing was examined* — the unconverted path, and an older API —
         * where `0` is *everything was priced and none of it was lost*. The
         * notice suppresses both, so folding them looks free; it is not, because
         * the next reader inherits a `0` that claims an all-clear nobody gave.
         */
        unpricedLines={unpricedLines}
      />

      {/*
       * ⚠️ **`awaitingFirstRead`, NOT the bare `loading` flag.** A search
       * keystroke sets `loading` while the rows already on screen stay exactly
       * where they are; passing it raw would swap a screen of
       * true-a-moment-ago figures for skeletons on every character typed.
       */}
      {/*
       * ⚠️ **NO `includeUncoded` PROP ANY MORE.** The headline now asks `groups`
       * whether the bucket is listed rather than being told which control was
       * set — because the Product column's funnel became a second way to take it
       * out, and a flag naming one control silently stopped covering the other.
       * See the tile's own comment for the 5733.7% it printed.
       */}
      <LayoutGrid
        layout={layout}
        render={{
          /*
           * The headline and its conversion footnote move together: the
           * footnote qualifies the tiles' figures, so it travels with them.
           */
          headline: () => (
            <div className="flex min-w-0 flex-col gap-4">
      <RevenueProductHeadline
        groups={groups}
        total={total}
        uncodedTotal={uncodedTotal}
        currency={figuresCurrency}
        taxLabel={taxLabel}
        loading={awaitingFirstRead}
      />

      {/*
        🔴 **HOW THESE FIGURES WERE ARRIVED AT — mounted on this tab for the
        first time, 2026-09-14.** Wording owned by `CONVERSION_APPROXIMATE_NOTE`
        in `@/lib/targets`; see it for the full reasoning.

        ⚠️ *SUPERSEDED, AND THE FOLLOW-UP IT DEFERS WAS DONE IN THIS COMMENT'S
        OWN COMMIT: "THAT CONSTANT'S OWN DOC STILL FORBIDS THIS TAB BY NAME —
        'NOT on Revenue by Company, Revenue by Product or Top Movers' — and it
        was right when written, because this tab filtered and converted nothing.
        `targets.ts` is owned elsewhere, so that sentence is reported as a
        required follow-up rather than edited here."* **That constant now
        PERMITS this tab by name**, and carries the old forbidding sentence
        marked `SUPERSEDED` beside the reversal — so there is no contradiction
        left for this comment to record.

        ⚠️ *SUPERSEDED 2026-09-18: "⛔ **THE STILL-TRUE HALF: Revenue by Company
        and Top Movers REMAIN FORBIDDEN**, and the reason is unchanged — they
        filter to one chosen currency and convert nothing … **The list lost one
        member; it was not retired.**"* **It was retired, four days later**, when
        `GET /revenue-tracker` gained a `baseCurrency` of its own and both tabs
        began converting. The list is empty and the gate below is the only test
        that matters — which is the lesson recorded at the constant itself: it was
        always a reading of which surfaces convert, never a policy about which
        ones may.

        ⚠️ **This is the shortest-range instance of an artefact falsified by a
        change somewhere else**: the two files moved in ONE commit, and only one
        of them was re-read. A comment naming another file's contents is a claim
        about that file, and nothing typechecks it.

        ⚠️ **GATED ON `converted`, WHICH READS THE SERVER'S ECHO.** Against an API
        predating `baseCurrency` nothing was converted, and a disclaimer about a
        conversion that did not happen is a false statement in the reassuring
        direction — the worse one.

        ⚠️ **AND ON THERE BEING FIGURES**, the same gate and the same reason the
        Financial Analytics tab uses: a disclaimer over a loading or empty tab
        qualifies nothing.

        ⚠️ **NEITHER A `warning` NOR A STRIP.** Nothing is wrong — the figures are
        correct and correctly labelled — so this takes the footnote register
        rather than the failure family, with the full statement on `title`.

        ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** It states a fact
        about the data the reader cannot otherwise see: nothing distinguishes a
        natively-summed total from a restated one, and the wrong conclusion it
        prevents — *"this total is what the accounts will show"* — is one a
        finance reader acts on. Same class as the kanban board's *"Indicative —
        mixed GBP, USD"*. It is attached to a FIGURE, not to a control.
      */}
      {converted && !awaitingFirstRead && rows.length > 0 && (
        <p className="m-0 text-2xs text-fg-subtle" title={CONVERSION_APPROXIMATE_NOTE.detail}>
          {CONVERSION_APPROXIMATE_NOTE.short}
        </p>
      )}
            </div>
          ),
          'cycle-strip': () => (
            <>
      {/*
       * ⚠️ **THE SIBLING TAB'S STRIP, IMPORTED RATHER THAN COPIED.** It answers
       * the identical question — what did each cycle contribute, in one
       * currency, for the rows on screen — and it is the cycle picker as well,
       * which is a piece of interaction two tabs must not implement twice.
       *
       * ⚠️ **ITS VISIBLE HEADING READS "Revenue by cycle" ON BOTH TABS, AND
       * THAT IS NOT FIXABLE FROM THIS FOLDER.** On Revenue by Company the bars
       * are companies' money; here they are products'. `RevenueCycleStrip`
       * hardcodes its `PanelHeader` and takes no `title` or `description` prop,
       * and it belongs to `revenue-company/` — so the fix is one prop on that
       * component, not a copy of it here. **Forking it would give this tab a
       * second implementation of the cycle picker**, which is the duplication
       * the import exists to prevent.
       *
       * ⚠️ **THE LANDMARK IS WHAT CAN BE FIXED FROM HERE.** A screen reader
       * navigating regions gets the population from this wrapper even though the
       * `h2` inside it does not carry it. It is half the answer, and it is
       * labelled as half rather than left to look like the whole.
       */}
      <section className="min-w-0" aria-label="Revenue by cycle, for the products on screen">
        <RevenueCycleStrip
          population="every cycle · products on screen"
          totals={cycleTotals}
          /* ⚠️ The unit the bars are IN — `cycleTotals` was summed through
           * `amountIn` against this same value, so labelling them from the
           * request would put a code on a figure that is not in it. */
          currency={figuresCurrency}
          selected={cycleKey}
          onSelect={(next) => setParam(PARAM.cycle, next)}
          loading={awaitingFirstRead}
        />
      </section>
            </>
          ),
          products: () => (
      <RevenueProductTable
        groups={groups}
        total={total}
        /* ⚠️ Every figure in this table came through `amountIn` against
         * `figuresCurrency`; the code beside them has to be the same one. */
        currency={figuresCurrency}
        taxLabel={taxLabel}
        scopeLabel={scopeLabel}
        /* See `order` above: derived from the RESOLVED order, never from
         * `table.sort`, so an unbacked key contradicts itself visibly. */
        orderNote={order === null ? 'ranked by paid revenue' : null}
        hiddenByColumnFilters={scopedGroups.length - table.visibleRows.length}
        headerFor={table.headerFor}
        loading={awaitingFirstRead}
        emptyReason={emptyReason}
        onClearFilters={clearFilters}
        scopeCycles={scopeCycles}
        todaysCycleId={todaysCycleId}
        coversAllCycles={scope.kind === 'all'}
      />
          ),
        }}
      />
    </div>
  );
}
